import { useApi } from '@/api';
import { Page, PageSkeleton } from '@/components';
import {
  DropdownFilter,
  Flex,
  TextSearchFilter,
  useTableProps,
  useIsMobile
} from '@blockanalitica/ui';
import WalletsTable from './WalletsTable';
import { DEFAULT_NETWORK } from '@/constants';

type WalletsAssetsData = [{ value: string; label: string }];

type WalletsAssetsResponse = {
  supplied_assets: WalletsAssetsData;
  borrowed_assets: WalletsAssetsData;
};

export default function WalletsPage() {
  const network = DEFAULT_NETWORK;
  const isMobile = useIsMobile();

  const { pagination, filters } = useTableProps({
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: '',
      selectedSupplyOption: '',
      selectedBorrowOption: ''
    }
  });

  const [data, error] = useApi<WalletsAssetsResponse>({
    url: '/wallets/assets/'
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page
        title="Wallets"
        actions={
          <Flex
            gap={['s', 'xs']}
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between">
            <Flex gap={['s', 'xs']}>
              <DropdownFilter
                multi={true}
                withCryptoIcon={true}
                filterField="selectedSupplyOption"
                options={data.supplied_assets}
                cryptoIconProps={{ network }}
                pagination={pagination}
                titleFormat={(filteredCount: number) =>
                  `Supplied assets (${filteredCount})`
                }
                {...filters}
              />
              <DropdownFilter
                multi={true}
                withCryptoIcon={true}
                filterField="selectedBorrowOption"
                options={data.borrowed_assets}
                cryptoIconProps={{ network }}
                pagination={pagination}
                titleFormat={(filteredCount: number) =>
                  `Borrowed assets (${filteredCount})`
                }
                {...filters}
              />
            </Flex>
            <TextSearchFilter
              pagination={pagination}
              placeholder="Search by wallet"
              flex="1"
              display="flex"
              width="100%"
              options={{
                variants: {
                  textSearchFilter: 'fullWidth'
                }
              }}
              {...filters}
            />
          </Flex>
        }>
        <WalletsTable
          url="/wallets/"
          tableFilters={{
            supplyAssetsFiltered:
              filters.filtersApplied['selectedSupplyOption'],
            borrowAssetsFiltered: filters.filtersApplied['selectedBorrowOption']
          }}
          textSearch={filters.filtersApplied['textSearch']}
          pagination={pagination}
        />
      </Page>
    );
  }

  return <PageSkeleton />;
}
